import { useState, useRef } from 'react';

import { useMovies } from './useMovies';
import { useLocalStorageState } from './useLocalStorageState';

import NavBar from './components/navBar/NavBar.js';
import Main from './components/main/Main.js';
import Box from './components/box/Box.js';
import MovieList from './components/movieList/MovieList.js';
import MovieDetails from './components/movieDetails/MovieDetails.js';
import Loader from './components/loader/Loader.js';
import Btn from './components/btn/Btn.js';
import PictureFrame from './components/pictureFrame/PictureFrame.js';
import Plug from './components/plug/Plug.js';

const average = (arr) =>
  arr.reduce((acc, cur, i, arr) => acc + cur / arr.length, 0);

function WatchedSummary({ watched }) {
  const avgImdbRating = average(watched.map((movie) => movie.imdbRating));
  const avgUserRating = average(watched.map((movie) => movie.userRating));
  const avgRuntime = average(watched.map((movie) => movie.runtime));

  return (
    <div className='summary'>
      <div className='summary__holder'>
        <h2>Movies you watched</h2>
        <div className='summary__box'>
          <p>
            <span>#️⃣</span>
            <span>{watched.length} movies</span>
          </p>
          <p>
            <span>⭐️</span>
            <span>{avgImdbRating.toFixed(2)}</span>
          </p>
          <p>
            <span>🌟</span>
            <span>{avgUserRating.toFixed(2)}</span>
          </p>
          <p>
            <span>⏳</span>
            <span>{Math.round(avgRuntime)} min</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function WatchedMoviesList({ watched, onDeleteWatched }) {
  return (
    <ul className='list'>
      {watched.map((movie) => (
        <WatchedMovie
          key={movie.imdbID}
          movie={movie}
          onDeleteWatched={onDeleteWatched}
        />
      ))}
    </ul>
  );
}

function WatchedMovie({ movie, onDeleteWatched }) {
  function handelClick() {
    onDeleteWatched(movie.imdbID);
  }

  return (
    <li>
      <div className='img-holder'>
        {movie.poster !== 'N/A' ? (
          <PictureFrame>
            <img src={movie.poster} alt={`${movie.title} poster`} />
          </PictureFrame>
        ) : (
          <Plug>{`${movie.poster}`}</Plug>
        )}
      </div>
      <h3>{movie.title}</h3>
      <div className='info'>
        <p>
          <span>⭐️</span>
          <span>{movie.imdbRating}</span>
        </p>
        <p>
          <span>🌟</span>
          <span>{movie.userRating}</span>
        </p>
        <p>
          <span>⏳</span>
          <span>{movie.runtime} min</span>
        </p>
        <Btn type='delete' onClick={() => onDeleteWatched(movie.imdbID)}>
          &times;
        </Btn>
      </div>
    </li>
  );
}

function ErrorMessage({ message }) {
  return (
    <p className='error'>
      <span>⛔</span>
      {message}
    </p>
  );
}

export default function App() {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [movies, isLoading, error] = useMovies(query, handelCloseMovie);
  const [watched, setWatched] = useLocalStorageState([], 'watched');
  const targetRef = useRef(null);
  const mediaQuery = window.matchMedia('(max-width: 767px)');

  function handelSelectMovie(id) {
    setSelectedId((selectedId) => (selectedId === id ? null : id));
  }

  function handelCloseMovie() {
    setSelectedId(null);
  }

  function handelAddWatched(movie) {
    setWatched((watched) => [...watched, movie]);
  }

  function handelDeleteWatched(id) {
    setWatched((watched) => watched.filter((movie) => movie.imdbID !== id));
  }

  function handleScrollToElement() {
    targetRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <>
      <NavBar query={query} onQuery={setQuery} movies={movies} />
      <Main>
        <Box>
          {isLoading && <Loader />}
          {!isLoading && !error && (
            <MovieList
              movies={movies}
              onSelectMovie={handelSelectMovie}
              onScroll={handleScrollToElement}
              mediaQuery={mediaQuery}
            />
          )}
          {error && <ErrorMessage message={error} />}
        </Box>
        <Box>
          <div ref={targetRef}>
            {selectedId ? (
              <MovieDetails
                selectedId={selectedId}
                onCloseMovie={handelCloseMovie}
                onAddWatched={handelAddWatched}
                watched={watched}
              />
            ) : (
              <>
                <WatchedSummary watched={watched} />
                <WatchedMoviesList
                  watched={watched}
                  onDeleteWatched={handelDeleteWatched}
                />
              </>
            )}
          </div>
        </Box>
      </Main>
    </>
  );
}
